import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Archive, ArchiveRestore, Pencil, Plus, Thermometer } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Field, GREEN } from '../bfsi/bfsiShared';
import { qrGet, qrPatch, qrPost } from './qrApi';

const EMPTY = { name: '', min_c: '', max_c: '', max_storage_days: '' };

function ProfileForm({ initial, onSaved, onCancel }) {
  const [f, setF] = useState(initial ? { name: initial.name, min_c: String(initial.min_c), max_c: String(initial.max_c), max_storage_days: String(initial.max_storage_days) } : EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF(s => ({ ...s, [k]: v }));
  const lo = parseFloat(f.min_c), hi = parseFloat(f.max_c), days = parseInt(f.max_storage_days, 10);
  const invalid = !f.name.trim() || Number.isNaN(lo) || Number.isNaN(hi) || lo >= hi || !(days >= 1);
  const submit = async () => {
    setBusy(true);
    try {
      const body = { name: f.name.trim(), min_c: lo, max_c: hi, max_storage_days: days };
      const r = initial ? await qrPatch(`/profiles/${initial.id}`, body) : await qrPost('/profiles', body);
      toast.success(initial ? 'Profile updated' : 'Profile created');
      onSaved(r);
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };
  return (
    <div className="rounded-lg border bg-white p-5" style={{ borderColor: '#E5E3DC' }} data-testid="qr-profile-form">
      <h3 className="font-bold mb-3">{initial ? `Edit ${initial.name}` : 'New temperature profile'}</h3>
      <div className="grid gap-3 md:grid-cols-4">
        <Field label="Name" value={f.name} onChange={set('name')} testId="qr-profile-name" placeholder="2-8 C" />
        <Field label="Min °C" value={f.min_c} onChange={set('min_c')} testId="qr-profile-min" type="number" step="0.5" />
        <Field label="Max °C" value={f.max_c} onChange={set('max_c')} testId="qr-profile-max" type="number" step="0.5" />
        <Field label="Max storage days" value={f.max_storage_days} onChange={set('max_storage_days')} testId="qr-profile-days" type="number" min="1" />
      </div>
      {!Number.isNaN(lo) && !Number.isNaN(hi) && lo >= hi && <p className="text-xs mt-2" style={{ color: '#B42318' }} data-testid="qr-profile-range-error">Min must be lower than max</p>}
      <div className="flex gap-2 mt-4">
        <Button data-testid="qr-profile-save" size="sm" className="h-8 text-white" style={{ background: GREEN }} disabled={invalid || busy} onClick={submit}>{initial ? 'Save' : 'Create'}</Button>
        <Button data-testid="qr-profile-cancel" size="sm" variant="outline" className="h-8" onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
}

export default function AdminQrProfilesPage() {
  const [rows, setRows] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [editing, setEditing] = useState(null); // null | 'new' | profile
  const load = useCallback(() => qrGet(`/profiles${showArchived ? '?include_archived=1' : ''}`).then(setRows).catch(e => toast.error(e.message)), [showArchived]);
  useEffect(() => { load(); }, [load]);
  const toggleArchive = async (p) => {
    try { await qrPatch(`/profiles/${p.id}`, { archived: !p.archived }); toast.success(p.archived ? 'Profile restored' : 'Profile archived'); load(); }
    catch (e) { toast.error(e.message); }
  };
  return (
    <div className="p-8 max-w-5xl space-y-6" data-testid="qr-profiles-page">
      <div className="flex items-start gap-4">
        <div className="mr-auto">
          <h1 className="text-4xl font-bold" style={{ color: '#1B1D1C' }}>Temperature profiles</h1>
          <p className="text-sm mt-1" style={{ color: '#6b7069' }}>Ranges a passport is held to. Profiles cannot be deleted — archive them to hide from issuance; passports already issued keep theirs.</p>
        </div>
        <Button data-testid="qr-profile-new" size="sm" className="h-9 text-white" style={{ background: GREEN }} onClick={() => setEditing('new')}><Plus size={14} className="mr-1" /> New profile</Button>
      </div>
      {editing && <ProfileForm initial={editing === 'new' ? null : editing} onSaved={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />}
      <label className="flex items-center gap-2 text-sm" style={{ color: '#4b504a' }}>
        <input type="checkbox" data-testid="qr-profiles-show-archived" checked={showArchived} onChange={e => setShowArchived(e.target.checked)} /> Show archived
      </label>
      <div className="rounded-lg border bg-white overflow-hidden" style={{ borderColor: '#E5E3DC' }}>
        <table className="w-full text-sm" data-testid="qr-profiles-table">
          <thead><tr className="text-left text-xs uppercase" style={{ background: '#F3F1EA', color: '#6b7069' }}>
            <th className="px-4 py-2">Name</th><th className="px-4 py-2">Range</th><th className="px-4 py-2">Max storage</th><th className="px-4 py-2">Passports</th><th className="px-4 py-2">Status</th><th className="px-4 py-2" />
          </tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan="6" className="px-4 py-8 text-center" style={{ color: '#9a9e96' }}>No profiles yet — create the first one</td></tr>}
            {rows.map(p => (
              <tr key={p.id} className="border-t" style={{ borderColor: '#F3F1EA', opacity: p.archived ? 0.6 : 1 }} data-testid={`qr-profile-row-${p.id}`}>
                <td className="px-4 py-2 font-semibold flex items-center gap-2"><Thermometer size={14} className="text-slate-400" /> {p.name}</td>
                <td className="px-4 py-2 font-mono">{p.min_c} to {p.max_c} °C</td>
                <td className="px-4 py-2">{p.max_storage_days} d</td>
                <td className="px-4 py-2">{p.passports_in_use}</td>
                <td className="px-4 py-2">{p.archived ? <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: '#F3F1EA', color: '#6b7069' }}>archived</span> : <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: '#E7F2EE', color: GREEN }}>active</span>}</td>
                <td className="px-4 py-2 text-right whitespace-nowrap">
                  <button data-testid={`qr-profile-edit-${p.id}`} className="text-xs underline mr-3" style={{ color: GREEN }} onClick={() => setEditing(p)}><Pencil size={12} className="inline mr-1" />Edit</button>
                  <button data-testid={`qr-profile-archive-${p.id}`} className="text-xs underline" style={{ color: '#6b7069' }} onClick={() => toggleArchive(p)}>
                    {p.archived ? <><ArchiveRestore size={12} className="inline mr-1" />Restore</> : <><Archive size={12} className="inline mr-1" />Archive</>}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
